import React, { useState, useRef, useEffect } from 'react';
import { CueTip } from '../../../components/Common/CueTooltip';
import FormHelper from '../../../components/FormBuilder/FormHelpers';
import { SelectListPlaceholder, FieldLabel, Description, RequiredErrMsg, WarningMsg, FieldReview, FieldPoint } from '../FieldLabel';
import { SpanRichTextView } from '../../Common/RichTextView';
import DataSourceService from '../../../services/DataSourceService';
import { Spinner } from '../../Common/Spinner';

const SEARCH_WAIT_INTERVAL = 500;

const InputAutoComplete = ({ labelLocale, renderField, field, position, formData, readOnly, errors, forceUpdate, formFn, initDataLoad, isFormWizard, openDataSourceModal, openFieldLabelModal, currentLocale, fieldReview }) => {
  const clientId = field.client_id;
  const labelId = clientId + '_label';
  const getKey = 'get_' + clientId;
  const setKey = 'set_' + clientId;

  let pagenate = formFn[clientId + '_pagenate'] || {};
  let path = formFn[clientId + '_path'] || 'label';
  let op_kv = field.options_key_value;

  const label = formData[labelId] || formData[clientId]
  let [search, setSearch] = useState(label);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noResultFound, setNoResultFound] = useState(false);
  const [showError, setShowError] = useState(false);
  const inputEl = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    setSearch(label)
  }, [label])

  useEffect(() => {
    try {
      const getFieldFn = formFn[getKey];
      if (renderField && !search && initDataLoad)
        getFieldFn(search, onSuccessCallback, onFailureCallback);
    } catch (e) {
      console.log(e)
    }
  }, [])

  if (!renderField) return null;

  let inputAttributes = {
    'type': 'text',
    'id': clientId,
    'name': clientId,
    'placeholder': labelLocale(5),
    'disabled': readOnly,
    'className': 'form-input form-input-gray form-input-full'
  }

  let parentAttributes = {
    'tooltip': field.tooltip
  }

  let labelAttributes = FormHelper.setLabelStyle(field, isFormWizard, parentAttributes);

  //FormHelper.setDisabled(field, inputAttributes);

  const handleChange = (event) => {
    clearTimeout(timerRef.current);
    const searchValue = event.target.value;
    setSearch(searchValue);
    pagenate.page = 1;
    timerRef.current = setTimeout(() => onSearch(false), SEARCH_WAIT_INTERVAL);
  };

  const onSearch = (onFocus) => {
    try {
      setNoResultFound(false)
      setLoading(true)
      const getFieldFn = formFn[getKey];
      if (getFieldFn) {
        getFieldFn(search || '', onSuccessCallback, onFailureCallback);
      }
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const onItemSelect = (item) => {
    const setFieldFn = formFn[setKey];
    if (setFieldFn) {
      setFieldFn(item);
    }
    setSearch(formData[labelId] || formData[clientId]);
    setList([]);
    setError(true);
    setTimeout(() => {
      forceUpdate();
    }, 0);
  }

  /*execute a function when someone clicks in the document:*/
  /*document.addEventListener("click", function (e) {
    setList([]);
  })*/

  const next = (e) => {
    e.preventDefault();
    if ((pagenate.page + 1) <= pagenate.pageSize) {
      pagenate.page += 1;
      onSearch();
    }
  }

  const previous = (e) => {
    e.preventDefault();
    if ((pagenate.page - 1) > 0) {
      pagenate.page -= 1;
      onSearch();
    }
  }

  const onSuccessCallback = (data) => {
    if (data.length == 0) setNoResultFound(true);
    setList(data);
    setLoading(false);
  }

  const onFailureCallback = (error) => {
    setList(error);
    setLoading(false);
  }

  const setupDataSourceFunctions = () => {
    if (formFn[getKey] == null) {
      formFn[getKey] = (search, onSuccessCallback, onFailureCallback) => {
        DataSourceService.searchDataSource({
          "page": pagenate.page || 1,
          "per_page": 100,
          "data_source_id": [field.data_source_id],
          "search": search,
          "is_form_field": true,
          "sort_column": 'id',
          "sort_direction": 'asc'
        }).then((res) => {
          pagenate.pageSize = res.meta.last_page;
          onSuccessCallback(res.data_source_lists);
        })
      }
    }

    if (formFn[setKey] == null) {
      formFn[setKey] = (item) => {
        formData[clientId] = item.value;
        formData[labelId] = item.label;
        formFn.ds[clientId] = item;
        FormHelper.checkCustomValidation(field, formData, formFn.ds, errors[clientId]);
        //formFn['on_select_'+field.client_id] && formFn['on_select_'+field.client_id](item, errors[field.client_id])
        setSearch(item.label);
      }
    }
  }

  /*Load data source dynamically*/
  if (field.data_source_id) {
    setupDataSourceFunctions();
  }

  const setError = (resetState) => {
    if (field.required) {
      const name = clientId;
      errors[name].invalid = formData[clientId] == null ? true : false;
      errors[name].touched = true;
      if (errors[name].invalid) {
        errors.invalid[name] = true;
      } else {
        delete errors.invalid[name]
      }
      if (resetState) {
        setShowError(!showError);
        formFn.refreshFormSubmit();
      }
    }
  }

  const removeFocus = () => {
    setList([]);
    inputEl.current.blur();
  }

  const clearSelection = () => {
    setSearch('');
    const clearFieldFn = formFn[`clear_${clientId}`];
    clearFieldFn && clearFieldFn();
    setError();
  }

  const Error = () => {
    const isFieldTouched = errors[field.client_id]?.touched || errors.isSubmitted;
    const isFieldInvalid = errors[field.client_id]?.invalid;
    const hasWarning = errors[field.client_id]?.warning_msg;

    if (field.required && isFieldTouched && isFieldInvalid) {
      return (
        <div className="errormsg">
          <div>
            <span className="fas fa-exclamation-triangle m-r-5" />
            <RequiredErrMsg {...{ field, labelLocale }} />
          </div>
        </div>
      );
    }

    if (noResultFound) {
      return (
        <div className="errormsg">
          <div>No Result Found</div>
        </div>
      );
    }

    if (hasWarning) {
      return (
        <div className="errormsg">
          <WarningMsg {...{ field, currentLocale }} lkey="gt_lt_msg" />
        </div>
      );
    }

    return null;
  };

  const highlight = (text, phrase) => {
    if (phrase && text) {
      text = text.toString();
      text = text.replace(new RegExp('(' + phrase + ')', 'gi'), '<strong>$1<strong>');
    }
    return <SpanRichTextView html={text} />;
  }

  const searchResult = list.map((r, k) =>
    <div key={k} onClick={e => onItemSelect(r)}>{highlight(r[path])}</div>
  )

  return (
    <>
      <div {...parentAttributes} onMouseLeave={() => removeFocus()}>
        <div className="flex coldir m-b-20 tooltip">
          <CueTip
            positionCss={position > 1 ? 'top' : 'bottom'}
            tooltip={field.tooltip}
            currentLocale={currentLocale}
            locale={field.locale?.tooltip} />
          <FieldLabel field={field}
            isFormWizard={isFormWizard}
            labelAttributes={labelAttributes}
            currentLocale={currentLocale}
            openFieldLabelModal={openFieldLabelModal} />
          <div className="autocomplete">
            {loading && <Spinner></Spinner>}
            <input {...inputAttributes} value={search} ref={inputEl}
              onChange={e => handleChange(e)}
              onFocus={() => onSearch(true)}
              autoComplete="off" />
            <i className="fas fa-times pos-abs lh-42 r-15" onClick={() => clearSelection()} />
            <div id="autocomplete-list" className="autocomplete-items">
              {(pagenate.pageSize > 1 && list.length > 0) &&
                <ul className="pager">
                  <li onClick={e => previous(e)}><a href="/">Previous</a></li>
                  <li onClick={e => next(e)}><a href="/">Next</a></li>
                  <small>{pagenate.page} of {pagenate.pageSize}</small>
                </ul>
              }
              {searchResult}
              {list.length > 0 && <div><SelectListPlaceholder field={field} currentLocale={currentLocale} /></div>}
            </div>
            <FieldPoint userRefId={formFn.userRefId} clientId={clientId}
              point={field.point} currentLocale={currentLocale}
              negPoint={field.incorrect_point} />
            <Description description={field.description} />
            <FieldReview review={fieldReview} />
            <Error />
          </div>
        </div>
      </div>
      {/*<APIRequestEle {...{formFn, field, formData}}/>*/}
    </>
  )
}

export default InputAutoComplete;